import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { toast } from "sonner";
import { Coins, Bitcoin, ArrowRight, ShieldCheck, Check, TrendingUp, Bot, Radio, Zap, Newspaper, CalendarClock, Send, Gift, Camera } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1605792657660-596af9009e82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const COUNTRIES = ["United Arab Emirates", "India", "United Kingdom", "Nigeria", "Pakistan", "South Africa", "Singapore"];
const TELEGRAM_HANDLE = "TFTDubai";

const FEATURES = [
  [Bot, "AI Gold & BTC Research", "Institutional-grade analysis for XAUUSD & BTCUSD, on demand — bias, key levels and the reasoning behind every view."],
  [Radio, "Live Auto Signals", "Ready-to-trade setups with clear entry, stop-loss and take-profit — no more guessing where to enter or exit."],
  [Zap, "Scalping Signals", "Fast 1M–5M scalp setups for active traders who want quick, high-frequency opportunities."],
  [Newspaper, "News & Impact Engine", "Know exactly how CPI, FOMC & NFP will move Gold and Bitcoin — before the candles print."],
  [CalendarClock, "Economic Calendar + News-Lock", "Live high-impact event countdown so you never get caught on the wrong side of a news spike."],
  [Send, "Premium Telegram VIP", "Every signal and update delivered instantly to your phone in our private VIP community."],
];

const STEPS = [
  [Coins, "1. Open a live account", "Open a trading account with our verified partner broker using the button below. Takes just a few minutes."],
  [Camera, "2. Send your screenshot", `Message your account confirmation screenshot to @${TELEGRAM_HANDLE} on Telegram.`],
  [Gift, "3. Get FREE access", "We instantly unlock full premium research, signals and VIP Telegram — completely free, for as long as you trade."],
];

export default function Landing() {
  const [brokers, setBrokers] = useState([]);
  const [country, setCountry] = useState("");
  const [interest, setInterest] = useState("Both");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [winrate, setWinrate] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    load();
    client.get("/public/winrate").then(({ data }) => setWinrate(data)).catch(() => {});
    client.get("/public/performance").then(({ data }) => setPerf(data)).catch(() => {});
  }, [country]);
  const load = () => client.get("/brokers/public" + (country ? `?country=${encodeURIComponent(country)}` : "")).then(({ data }) => setBrokers(data)).catch(() => {});

  const submitLead = async (e) => {
    e.preventDefault();
    try { await client.post("/public/lead", { ...form, country, interest, source: "Website Signup", campaign: "homepage-signup" }); setSubmitted(true); toast.success("Registered! Check the next step below."); }
    catch { toast.error("Submission failed"); }
  };

  const openBroker = async (slug) => {
    try { const { data } = await client.get(`/go/${slug}?utm_source=landing&campaign=open-account`); if (data.redirect) window.open(data.redirect, "_blank"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }}>
      <header className="h-16 glass sticky top-0 z-20 flex items-center px-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-2"><div className="w-8 h-8 bg-gold text-black flex items-center justify-center font-head font-bold">F</div><span className="font-head font-bold text-white">Fateh FX</span></div>
        <Link to="/results" className="ml-auto text-sm text-slate-300 hover:text-gold transition-colors duration-200 flex items-center gap-1" data-testid="nav-results">Live Results</Link>
        <Link to="/login" className="ml-6 text-sm text-slate-300 hover:text-gold transition-colors duration-200 flex items-center gap-1">Sign in <ArrowRight size={14} /></Link>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--border-subtle)]">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url('${HERO}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(5,5,10,0.92), rgba(5,5,10,0.6))" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-gold text-gold text-xs font-mono uppercase tracking-widest mb-6">
            <span className="live-dot w-1.5 h-1.5 rounded-full bg-gold" />XAUUSD · BTCUSD Focused
          </div>
          <h1 className="font-head text-4xl md:text-6xl font-bold text-white tracking-tight max-w-4xl leading-[1.05]">
            The Gold & Bitcoin edge <span className="text-gold">every serious trader</span> needs
          </h1>
          <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed text-base md:text-lg">
            AI research, live signals, scalping setups, news-impact alerts and a premium Telegram community — the complete Gold & Bitcoin desk in one place. <span className="text-white font-semibold">Get it all FREE</span> when you open an account with our partner broker.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-8">
            <a href="#access" data-testid="hero-get-access" className="btn-gold px-5 py-3.5 sm:py-3 text-sm flex items-center justify-center gap-2">Get FREE Access <ArrowRight size={16} /></a>
            <a href={`https://t.me/${TELEGRAM_HANDLE}`} target="_blank" rel="noreferrer" data-testid="hero-telegram" className="px-5 py-3.5 sm:py-3 text-sm flex items-center justify-center gap-2 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200"><Send size={15} />Message @{TELEGRAM_HANDLE}</a>
          </div>
          <div className="flex flex-wrap gap-6 mt-8">
            {[["AI Research", Bot], ["Live Signals", Radio], ["Scalping", Zap], ["Premium Telegram", Send]].map(([l, I]) => (
              <div key={l} className="flex items-center gap-2 text-slate-300 text-sm"><I size={16} className="text-gold" />{l}</div>
            ))}
          </div>
          {winrate && winrate.has_data && (
            <div data-testid="winrate-badge" className="mt-10 inline-flex flex-wrap items-stretch gap-0 border border-gold/40 glass overflow-hidden">
              <div className="px-6 py-4 bg-[rgba(212,175,55,0.12)]">
                <div className="text-[10px] uppercase tracking-widest text-gold/80 mb-1 flex items-center gap-1"><span className="live-dot w-1.5 h-1.5 rounded-full bg-gold" />{winrate.days}-Day Win Rate</div>
                <div className="font-head text-4xl font-bold text-gold leading-none" data-testid="winrate-value">{winrate.win_rate}%</div>
              </div>
              <div className="px-6 py-4 flex items-center gap-6 border-l border-gold/20">
                <div><div className="text-lg font-bold text-white">{winrate.wins}<span className="text-slate-500">/{winrate.total_trades}</span></div><div className="text-[10px] uppercase tracking-wider text-slate-400">Winning trades</div></div>
                <div><div className={`text-lg font-bold ${winrate.net_pips >= 0 ? "text-emerald-400" : "text-red-400"}`}>{winrate.net_pips >= 0 ? "+" : ""}{winrate.net_pips}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Net pips</div></div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features — why every trader needs this */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-gold text-xs font-mono uppercase tracking-widest mb-3">Your complete trading desk</div>
          <h2 className="font-head text-3xl md:text-4xl font-bold text-white tracking-tight">Stop trading blind on Gold & Bitcoin</h2>
          <p className="text-slate-400 mt-4 text-base">Whether you scalp the 1-minute or swing the daily, these tools give you the same edge the pros use — clarity, timing, and discipline.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(([I, title, desc]) => (
            <div key={title} className="surface p-6 surface-hover transition-colors duration-200" data-testid={`feature-${title}`}>
              <div className="w-11 h-11 flex items-center justify-center bg-[rgba(212,175,55,0.12)] border border-[color:var(--border-subtle)] mb-4"><I size={20} className="text-gold" /></div>
              <h3 className="font-head font-semibold text-white text-lg">{title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How to get FREE access */}
      <section id="access" className="border-y border-[color:var(--border-subtle)]" style={{ background: "var(--surface)" }}>
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-gold text-gold text-xs font-mono uppercase tracking-widest mb-4"><Gift size={13} />100% Free Access</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-white tracking-tight">Get everything free in 3 simple steps</h2>
            <p className="text-slate-400 mt-4 text-base">No subscription. No hidden fees. Just open an account with our partner broker and the entire platform is yours.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {STEPS.map(([I, title, desc], i) => (
              <div key={title} className="surface p-6 relative" data-testid={`step-${i + 1}`}>
                <div className="w-11 h-11 flex items-center justify-center bg-gold text-black mb-4"><I size={20} /></div>
                <h3 className="font-head font-semibold text-white text-lg">{title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="surface p-5 flex flex-col sm:flex-row items-center gap-4 border border-gold/40">
            <Send size={22} className="text-gold shrink-0" />
            <div className="text-sm text-slate-300 text-center sm:text-left flex-1">
              After opening your account, send your screenshot to <span className="text-gold font-semibold">@{TELEGRAM_HANDLE}</span> on Telegram to activate your free premium access instantly.
            </div>
            <a href={`https://t.me/${TELEGRAM_HANDLE}`} target="_blank" rel="noreferrer" data-testid="steps-telegram" className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2 shrink-0">Open Telegram <ArrowRight size={15} /></a>
          </div>
        </div>
      </section>

      {/* Today's performance */}
      {perf && (perf.today.has_data || perf.week.has_data) && (
        <section className="max-w-7xl mx-auto px-6 pt-4 pb-2" data-testid="perf-section">
          <div className="glass border border-gold/30 p-6 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <h2 className="font-head text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
                <span className="live-dot w-2 h-2 rounded-full bg-gold" /> Live Performance
              </h2>
              <div className="flex gap-6">
                <div><div className="text-2xl font-bold text-gold">{perf.week.win_rate}%</div><div className="text-[10px] uppercase tracking-wider text-slate-400">7-day win rate</div></div>
                <div><div className={`text-2xl font-bold ${perf.week.net_pips >= 0 ? "text-emerald-400" : "text-red-400"}`}>{perf.week.net_pips >= 0 ? "+" : ""}{perf.week.net_pips}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">7-day net pips</div></div>
                <div><div className="text-2xl font-bold text-white">{perf.week.signals}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Signals</div></div>
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Today's signals {perf.today.has_data && <span className="text-gold">· Net {perf.today.net_pips >= 0 ? "+" : ""}{perf.today.net_pips} pips</span>}</div>
            {perf.today.has_data ? (
              <div className="space-y-1.5" data-testid="perf-today">
                {perf.today.trades.map((t, i) => <div key={i} className="text-sm text-slate-200 font-mono bg-[color:var(--app-bg)] px-3 py-2 border border-[color:var(--border-subtle)]">{t}</div>)}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Setups are being tracked live — new signals drop through the day. 👇 Open an account to get them with full entry & SL.</p>
            )}
            <p className="text-[11px] text-slate-600 mt-3">Open an account below to receive every signal live with entry, stop-loss & targets.</p>
            <Link to="/results" data-testid="view-results-link" className="mt-3 inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold/80 transition-colors duration-200">See all live results, running signals & win-rate <ArrowRight size={14} /></Link>
          </div>
        </section>
      )}

      {/* Selector + brokers */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-head text-2xl font-semibold text-white tracking-tight mb-1">Open your account & unlock free access</h2>
          <p className="text-sm text-slate-500 mb-5">Pick your country and market, open with a verified partner below, then send your screenshot to @{TELEGRAM_HANDLE}.</p>

          <div className="flex flex-wrap gap-3 mb-6">
            <select data-testid="landing-country" value={country} onChange={(e) => setCountry(e.target.value)} className="bg-[color:var(--surface)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
              <option value="">All countries</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex border border-[color:var(--border-subtle)]">
              {["Gold", "Bitcoin", "Both"].map((i) => <button key={i} onClick={() => setInterest(i)} data-testid={`landing-interest-${i}`} className={`px-3 py-2 text-sm transition-colors duration-200 ${interest === i ? "bg-gold text-black" : "text-slate-400"}`}>{i}</button>)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {brokers.length === 0 && <div className="text-sm text-slate-500 col-span-2">No brokers for this filter.</div>}
            {brokers.map((b) => (
              <div key={b.id} className="surface p-5" data-testid={`landing-broker-${b.slug}`}>
                <div className="flex items-center gap-3">
                  {b.logo
                    ? <img src={b.logo} alt={b.name} className="w-11 h-11 object-contain bg-white/5 rounded" />
                    : <div className="w-11 h-11 bg-gold text-black flex items-center justify-center font-head font-bold text-lg">{b.name[0]}</div>}
                  <div><div className="font-head font-semibold text-white text-lg">{b.name}</div><div className="text-xs text-slate-500">{b.platform}</div></div>
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-slate-300"><Check size={14} className="text-bull" />XAUUSD & BTCUSD available</div>
                  <div className="flex items-center gap-2 text-slate-300"><Check size={14} className="text-bull" />{(b.countries || []).includes("All") ? "Available worldwide" : (b.countries || []).slice(0, 3).join(", ")}</div>
                </div>
                <div className="text-[11px] text-slate-500 italic mt-3">{b.regulation}</div>
                <button data-testid={`open-account-${b.slug}`} onClick={() => openBroker(b.slug)} className="btn-gold w-full py-2.5 text-sm mt-4 flex items-center justify-center gap-2">Open Account <ArrowRight size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Signup form */}
        <div>
          <div className="surface p-6 sticky top-24" data-testid="landing-lead-form">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-gold text-gold text-[10px] font-mono uppercase tracking-widest mb-3"><Gift size={11} />Free Signup</div>
            <h3 className="font-head text-xl font-semibold text-white">Create your free account</h3>
            <p className="text-sm text-slate-500 mt-1 mb-5">Register in seconds — we'll help you open a broker account and unlock full research access, free.</p>
            {submitted ? (
              <div className="text-center py-6">
                <ShieldCheck size={34} className="text-bull mx-auto mb-3" />
                <div className="text-white font-head text-lg">You're registered! 🎉</div>
                <div className="text-sm text-slate-400 mt-2 leading-relaxed">Next: open your account with our partner broker, then send your screenshot to <span className="text-gold font-semibold">@{TELEGRAM_HANDLE}</span> on Telegram to activate free premium access.</div>
                <a href={`https://t.me/${TELEGRAM_HANDLE}`} target="_blank" rel="noreferrer" data-testid="success-telegram" className="btn-gold px-4 py-2.5 text-sm inline-flex items-center gap-2 mt-4">Open Telegram <ArrowRight size={15} /></a>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-3">
                {["name", "email", "phone"].map((f) => (
                  <input key={f} required={f !== "phone"} type={f === "email" ? "email" : "text"} placeholder={f === "phone" ? "Phone / WhatsApp" : f[0].toUpperCase() + f.slice(1)} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    data-testid={`landing-input-${f}`} className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-white focus:border-gold focus:outline-none" />
                ))}
                <select data-testid="landing-form-country" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-white focus:border-gold focus:outline-none">
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-2">
                  {["Gold", "Bitcoin", "Both"].map((i) => <button type="button" key={i} onClick={() => setInterest(i)} data-testid={`landing-form-interest-${i}`} className={`flex-1 px-3 py-2 text-sm border transition-colors duration-200 ${interest === i ? "border-gold text-gold" : "border-[color:var(--border-subtle)] text-slate-400"}`}>{i}</button>)}
                </div>
                <button data-testid="landing-submit" className="btn-gold w-full py-2.5 text-sm">Register Free</button>
              </form>
            )}
            <p className="text-[11px] text-slate-600 mt-4 leading-relaxed">Risk disclosure: Trading involves substantial risk. This is research only and not financial advice. No profits are guaranteed. Broker regulatory status must be independently verified.</p>
          </div>
        </div>
      </section>

      {/* Testimonials / proof */}
      <section className="border-t border-[color:var(--border-subtle)]" style={{ background: "var(--surface)" }}>
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[["12,000+", "Traders in community"], ["2", "Markets, fully focused"], ["24/5", "Signals & research"], ["100%", "Free with partner broker"]].map(([n, l]) => (
              <div key={l} className="text-center">
                <div className="font-head text-3xl md:text-4xl font-bold text-gold">{n}</div>
                <div className="text-xs text-slate-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-gold text-xs font-mono uppercase tracking-widest mb-3">What traders say</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-white tracking-tight">Loved by Gold & Bitcoin traders</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["Ahmed R.", "Dubai", "The scalping signals on Gold changed my mornings. Clear entries, clear stops — I finally stopped over-trading."],
              ["Priya N.", "Mumbai", "The news-lock alerts saved me before the last CPI. This platform pays for itself and it's completely free."],
              ["Daniel O.", "Lagos", "AI research explains the 'why' behind every setup. As a beginner, that's exactly what I needed to grow."],
            ].map(([name, city, quote]) => (
              <div key={name} className="surface p-6" data-testid={`testimonial-${name}`}>
                <div className="flex gap-1 text-gold mb-3">{"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}</div>
                <p className="text-sm text-slate-300 leading-relaxed">"{quote}"</p>
                <div className="flex items-center gap-3 mt-4">
                  <div className="w-9 h-9 bg-gold text-black flex items-center justify-center font-head font-bold">{name[0]}</div>
                  <div><div className="text-sm text-white">{name}</div><div className="text-xs text-slate-500">{city}</div></div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-600 mt-8 text-center">Testimonials reflect individual experiences and are not a guarantee of results. Trading involves risk.</p>
          <div className="text-center mt-8">
            <a href="#access" className="btn-gold px-6 py-3 text-sm inline-flex items-center gap-2">Get FREE Access Now <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[color:var(--border-subtle)] py-8 text-center text-xs text-slate-600">
        Fateh FX · Gold & Bitcoin research desk · Research only, no guaranteed profits · Trading involves risk
      </footer>
    </div>
  );
}
